import {
  type $Fetch,
  type FetchOptions,
  type FetchRequest,
  type FetchResponse,
  type MappedResponseType,
  ofetch,
  type ResponseType,
} from 'ofetch';
import type { Authentication } from '@/generic/authentication/domain/Authentication.ts';

export class AuthenticatedFetcher {
  readonly #ofetchInstance: $Fetch;
  readonly #authentication: Authentication;

  constructor({
    options = {},
    authentication,
  }: {
    options?: FetchOptions;
    authentication: Authentication;
  }) {
    this.#authentication = authentication;
    this.#ofetchInstance = ofetch.create(options);
  }

  async fetch<T, R extends ResponseType = 'json'>(
    request: FetchRequest,
    options?: FetchOptions<R>,
  ): Promise<MappedResponseType<R, T>> {
    return this.#ofetchInstance(request, await this.authorizeOptions(options));
  }

  async raw<T, R extends ResponseType = 'json'>(
    request: FetchRequest,
    options?: FetchOptions<R>,
  ): Promise<FetchResponse<MappedResponseType<R, T>>> {
    return this.#ofetchInstance.raw<T, R>(request, await this.authorizeOptions(options));
  }

  private async listHeaders<R extends ResponseType>(options?: FetchOptions<R>) {
    const token = await this.#authentication.getToken();

    return token
      .map<HeadersInit | undefined>((token) => ({
        ...options?.headers,
        Authorization: token.bearer,
      }))
      .orElse(options?.headers);
  }

  private async authorizeOptions<R extends ResponseType>(options?: FetchOptions<R>) {
    return {
      ...options,
      headers: await this.listHeaders(options),
    };
  }
}
